import { useCallback, useMemo, useState } from "react"
import {
  createFileRoute,
  redirect,
  useMatches,
  useNavigate,
  useParams,
} from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Hash, Lock, Megaphone, Pin, Plus } from "lucide-react"
import { ChannelThread } from "@/components/channels/ChannelThread"
import { NewChannelDialog } from "@/components/channels/NewChannelDialog"
import { ChannelSettingsDialog } from "@/components/channels/ChannelSettingsDialog"
import { ThreadPanel } from "@/components/message/ThreadPanel"
import type { ColumnSpec } from "@/lib/column-layout"
import {
  parseRouteFocus,
  RoutedCollectionLayout,
  useRouteRecordSelection,
} from "@/lib/routed-collection-layout"
import { ConversationListView } from "@/components/messaging/ConversationListView"
import { resolveEventId, useActiveEvent } from "@/core/active-event"
import { patchResourceCache } from "@/core/cache"
import {
  channelsQueryOptions,
  isGuestFacing,
  patchChannel,
  properChannelName,
  relTime,
  type Conversation,
} from "@/core/channels"
import { conversationMessagesQueryOptions } from "@/core/conversations"

// The Channels LAYOUT route. AUDIENCE (guest vs internal) lives in the URL path;
// route DEPTH = Main columns (mirrors forms.tsx):
//   /channels/{audience}                       → col 1 (channel list)
//   /channels/{audience}/$channelId            → col 1 + 2 (+ channel thread)
//   /channels/{audience}/$channelId/thread/$id → col 1 + 2 + 3 (+ reply thread)
// `$channelId`/`$threadId` live on MARKER child routes; ColumnLayout decides how
// many fit. `?focus=2` fullscreens col 2. The bare `/channels` path redirects to
// `/channels/guest`; legacy `?channelId=…` redirects into the guest path.
export const Route = createFileRoute("/$orgSlug/$eventSlug/channels")({
  validateSearch: (s: Record<string, unknown>): { focus?: number; channelId?: string } => {
    return {
      focus: parseRouteFocus(s.focus),
      channelId: typeof s.channelId === "string" ? s.channelId : undefined,
    }
  },
  beforeLoad: ({ params, search, location }) => {
    if (search.channelId) {
      throw redirect({
        to: "/$orgSlug/$eventSlug/channels/guest/$channelId",
        params: { ...params, channelId: search.channelId },
        search: { focus: search.focus },
      })
    }
    // Bare `/channels` (no audience segment) → default to the guest audience.
    // Guard so `/channels/guest` + `/channels/internal` don't loop-redirect.
    if (location.pathname.replace(/\/+$/, "").endsWith("/channels")) {
      throw redirect({
        to: "/$orgSlug/$eventSlug/channels/guest",
        params,
        search: { focus: search.focus },
      })
    }
  },
  loader: ({ context, params }) => {
    void resolveEventId(params.orgSlug, params.eventSlug).then((eventId) => {
      if (!eventId) return
      void context.queryClient
        .ensureQueryData(channelsQueryOptions(eventId))
        .catch(() => undefined)
    })
  },
  component: ChannelsView,
})

type ChannelFilter = "active" | "all" | "archived"

function ChannelsView() {
  const params = useParams({ from: "/$orgSlug/$eventSlug/channels" })
  const { focus } = Route.useSearch()
  const navigate = useNavigate()
  const { eventId } = useActiveEvent()
  const qc = useQueryClient()

  const [creating, setCreating] = useState(false)
  const [query, setQuery] = useState("")
  const [filter, setFilter] = useState<ChannelFilter>("active")

  // AUDIENCE comes from the URL path: any matched route under
  // `/channels/internal` → internal, otherwise guest (the default segment).
  const matches = useMatches()
  const audience: "guest" | "internal" = useMemo(
    () =>
      matches.some((m) => m.routeId.includes("/channels/internal"))
        ? "internal"
        : "guest",
    [matches],
  )
  // Channel settings is a route-driven column (col 3): /…/$channelId/settings.
  const settingsActive = useMemo(
    () => matches.some((m) => m.routeId.endsWith("/settings")),
    [matches],
  )

  const { channelId: selectedId, threadId } = useRouteRecordSelection("channelId", "threadId")

  // Audience-aware navigation. Conditional literal `to:` strings keep the
  // TanStack route types valid (guest vs internal variants).
  const openChannel = useCallback(
    (id: string) => {
      // Opening a channel dismisses the in-progress "New channel" panel so the
      // selected channel takes its place instead of stacking beside it.
      setCreating(false)
      void navigate(
        audience === "internal"
          ? {
              to: "/$orgSlug/$eventSlug/channels/internal/$channelId",
              params: { ...params, channelId: id },
              search: {},
            }
          : {
              to: "/$orgSlug/$eventSlug/channels/guest/$channelId",
              params: { ...params, channelId: id },
              search: {},
            },
      )
    },
    [navigate, params, audience],
  )
  const closePane = useCallback(
    () =>
      void navigate(
        audience === "internal"
          ? { to: "/$orgSlug/$eventSlug/channels/internal", params, search: {} }
          : { to: "/$orgSlug/$eventSlug/channels/guest", params, search: {} },
      ),
    [navigate, params, audience],
  )
  // Reply-thread (col 3) navigation — keyed off the open channel.
  const openThread = useCallback(
    (messageId: string) => {
      if (!selectedId) return
      void navigate(
        audience === "internal"
          ? {
              to: "/$orgSlug/$eventSlug/channels/internal/$channelId/thread/$threadId",
              params: { ...params, channelId: selectedId, threadId: messageId },
              search: {},
            }
          : {
              to: "/$orgSlug/$eventSlug/channels/guest/$channelId/thread/$threadId",
              params: { ...params, channelId: selectedId, threadId: messageId },
              search: {},
            },
      )
    },
    [navigate, params, selectedId, audience],
  )
  const closeThread = useCallback(() => {
    if (!selectedId) return
    void navigate(
      audience === "internal"
        ? {
            to: "/$orgSlug/$eventSlug/channels/internal/$channelId",
            params: { ...params, channelId: selectedId },
            search: {},
          }
        : {
            to: "/$orgSlug/$eventSlug/channels/guest/$channelId",
            params: { ...params, channelId: selectedId },
            search: {},
          },
    )
  }, [navigate, params, selectedId, audience])
  // Channel settings opens as the deepest column (col 3). NO focus → the layout
  // shows it BESIDE the list + thread when they fit (collapsing to settings-only
  // on a narrow pane), instead of fullscreening it solo.
  const openSettings = useCallback(() => {
    if (!selectedId) return
    void navigate(
      audience === "internal"
        ? {
            to: "/$orgSlug/$eventSlug/channels/internal/$channelId/settings",
            params: { ...params, channelId: selectedId },
            search: {},
          }
        : {
            to: "/$orgSlug/$eventSlug/channels/guest/$channelId/settings",
            params: { ...params, channelId: selectedId },
            search: {},
          },
    )
  }, [navigate, params, selectedId, audience])
  const closeSettings = useCallback(() => {
    if (!selectedId) return
    void navigate(
      audience === "internal"
        ? {
            to: "/$orgSlug/$eventSlug/channels/internal/$channelId",
            params: { ...params, channelId: selectedId },
            search: {},
          }
        : {
            to: "/$orgSlug/$eventSlug/channels/guest/$channelId",
            params: { ...params, channelId: selectedId },
            search: {},
          },
    )
  }, [navigate, params, selectedId, audience])

  const channelsQ = useQuery({ ...channelsQueryOptions(eventId ?? ""), enabled: !!eventId })
  const channels = channelsQ.data ?? []

  // On create: close the form column, then open the new channel under ITS OWN
  // audience (guest vs internal) once the list refetch resolves — a new internal
  // channel opens under /internal even when created from the guest tab.
  const handleChannelCreated = useCallback(
    (name: string | null) => {
      setCreating(false)
      if (!name) return
      void qc.invalidateQueries({ queryKey: ["resource", "conversations"] }).then(() => {
        const fresh = (
          qc.getQueryData(channelsQueryOptions(eventId ?? "").queryKey) as
            | { data: Conversation[] }
            | undefined
        )?.data?.find((c) => c.name === name)
        if (!fresh) return
        void navigate(
          isGuestFacing(fresh)
            ? {
                to: "/$orgSlug/$eventSlug/channels/guest/$channelId",
                params: { ...params, channelId: fresh.id },
                search: {},
              }
            : {
                to: "/$orgSlug/$eventSlug/channels/internal/$channelId",
                params: { ...params, channelId: fresh.id },
                search: {},
              },
        )
      })
    },
    [qc, eventId, navigate, params],
  )

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return channels
      .filter((c) => (audience === "internal" ? !isGuestFacing(c) : isGuestFacing(c)))
      .filter((c) =>
        filter === "active" ? !c.archivedAt : filter === "archived" ? !!c.archivedAt : true,
      )
      .filter((c) =>
        q
          ? `${c.name ?? ""} ${c.topic ?? ""}`.toLowerCase().includes(q)
          : true,
      )
      .slice()
      .sort((a, b) => {
        // Pinned-first within active, then most-recent activity.
        const pin = (b.pinnedAtTop === 1 ? 1 : 0) - (a.pinnedAtTop === 1 ? 1 : 0)
        if (pin) return pin
        return channelActivityAt(b).localeCompare(channelActivityAt(a))
      })
  }, [channels, query, filter, audience])

  const selected = useMemo(
    () => channels.find((c) => c.id === selectedId) ?? null,
    [channels, selectedId],
  )

  const list = (
    <ConversationListView
      title={audience === "internal" ? "Internal channels" : "Guest channels"}
      newLabel="New channel"
      onNew={() => setCreating(true)}
      search={query}
      onSearchChange={setQuery}
      searchPlaceholder="Find a channel…"
      filters={[
        { id: "active", label: "Active" },
        { id: "all", label: "All" },
        { id: "archived", label: "Archived" },
      ]}
      activeFilter={filter}
      onFilterChange={(id) => setFilter(id as ChannelFilter)}
      rows={filtered}
      getRowKey={(c) => c.id}
      loading={channelsQ.isLoading}
      renderRow={(c) => (
        <ChannelListRow
          channel={c}
          active={c.id === selectedId}
          onOpen={() => openChannel(c.id)}
          onPrefetch={() => {
            if (!eventId) return
            void qc.prefetchQuery(conversationMessagesQueryOptions(eventId, c.id)).catch(() => undefined)
          }}
        />
      )}
      empty={
        <EmptyState
          hasAny={channels.length > 0}
          archived={filter === "archived"}
          onNew={() => setCreating(true)}
        />
      }
    />
  )

  const columns = useMemo<ColumnSpec[]>(() => {
    const cols: ColumnSpec[] = [{ id: "list", minWidth: 320, width: 340, node: list }]
    // Creating a channel opens AS column 2 (beside the list when there's room,
    // fullscreen on a narrow pane) instead of a centered modal.
    if (creating) {
      cols.push({
        id: "new-channel",
        minWidth: 380,
        fill: true,
        node: (
          <NewChannelDialog
            variant="panel"
            open
            eventId={eventId}
            audience={audience}
            onOpenChange={(o) => !o && setCreating(false)}
            onCreated={handleChannelCreated}
          />
        ),
      })
      return cols
    }
    if (selected) {
      // Reply threads on every channel — the guest apps (iOS + Android) ship
      // thread sheets, so staff can thread in guest-facing channels too.
      cols.push({
        id: "thread",
        minWidth: 400,
        fill: true,
        node: (
          <ChannelThread
            key={selected.id}
            channel={selected}
            onBack={closePane}
            closeVariant="close"
            onOpenSettings={openSettings}
            onOpenThread={openThread}
          />
        ),
      })
      // Col 3 = channel settings (route-driven) OR a reply thread — mutually
      // exclusive (you're never in both at once).
      if (settingsActive) {
        cols.push({
          id: "settings",
          minWidth: 380,
          width: 460,
          node: (
            <ChannelSettingsDialog
              key={`settings-${selected.id}`}
              variant="panel"
              open
              channel={selected}
              onOpenChange={(o) => !o && closeSettings()}
              onRenamed={() => {
                void qc.invalidateQueries({ queryKey: ["resource", "conversations"] })
              }}
              onArchivedToggled={(nowArchived) => {
                if (nowArchived) closePane()
                void qc.invalidateQueries({ queryKey: ["resource", "conversations"] })
              }}
            />
          ),
        })
      } else if (threadId) {
        cols.push({
          id: "reply-thread",
          minWidth: 360,
          width: 400,
          node: (
            <ThreadPanel
              key={threadId}
              eventId={eventId ?? ""}
              conversationId={selected.id}
              parentMessageId={threadId}
              surfaceKind="channel"
              open
              onClose={closeThread}
              variant="panel"
            />
          ),
        })
      }
    }
    return cols
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, creating, selected, settingsActive, closePane, openSettings, closeSettings, openThread, threadId, eventId, audience, closeThread, qc, handleChannelCreated])

  return <RoutedCollectionLayout columns={columns} focus={focus} />
}

// A thin channel row mirroring the DM list look: a glyph (emoji or hash) +
// name + topic/last-activity + pinned/lock badges. Guest-facing channels get
// the accent guest-icon treatment; staff-only get a muted hash.
function ChannelListRow({
  channel,
  active,
  onOpen,
  onPrefetch,
}: {
  channel: Conversation
  active: boolean
  onOpen: () => void
  onPrefetch?: () => void
}) {
  const qc = useQueryClient()
  const guest = isGuestFacing(channel)
  const muted = channel.postingMode === "organizers-only"
  const pinned = channel.pinnedAtTop === 1 && !channel.archivedAt
  const stamp = relTime(channelActivityAt(channel))
  const subtitle = channel.topic?.trim() || (guest ? "Guest-facing" : "Staff only")

  // Mirrors ChannelThread's togglePin: PATCH pinnedAtTop, then surgically splice
  // the updated conversation row into the shared list cache (which re-sorts
  // pinned-first) instead of refetching; fall back to invalidate if the splice
  // misses (row not in this cache shape).
  const togglePin = useMutation({
    mutationFn: () =>
      patchChannel(channel.id, { pinnedAtTop: channel.pinnedAtTop === 1 ? 0 : 1 }),
    onSuccess: (data) => {
      const spliced = data ? patchResourceCache(qc, "conversations", data) : false
      if (!spliced) void qc.invalidateQueries({ queryKey: ["resource", "conversations"] })
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Pin failed"),
  })

  return (
    <button
      onClick={onOpen}
      onPointerEnter={() => {
        if (active) return
        onPrefetch?.()
      }}
      className={
        "group flex w-full items-center gap-3 px-3 py-2 text-left transition " +
        (active
          ? "bg-plum-100/70 dark:bg-white/10"
          : "hover:bg-plum-100/50 dark:hover:bg-white/5")
      }
    >
      <Glyph emoji={channel.emoji} guest={guest} muted={muted} />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="min-w-0 truncate text-[13.5px] font-semibold text-plum-900 dark:text-plum-50">
            {properChannelName(channel.name)}
          </span>
          {muted && (
            <Lock
              className="h-3 w-3 shrink-0 text-plum-700/40 dark:text-plum-200/30"
              aria-label="Organizers post only"
            />
          )}
          {channel.archivedAt && (
            <span className="shrink-0 rounded bg-plum-100 px-1 text-[9px] font-bold tracking-wide text-plum-700/60 uppercase dark:bg-white/10 dark:text-plum-200/45">
              archived
            </span>
          )}
          {/* Pin toggle: always shown + filled accent when pinned; hover-revealed
              when not. stopPropagation so it never opens the channel. Hidden for
              archived channels (can't pin an archived row). */}
          {!channel.archivedAt && (
            <span
              role="button"
              tabIndex={0}
              aria-pressed={pinned}
              title={pinned ? "Unpin channel" : "Pin channel"}
              onClick={(e) => {
                e.stopPropagation()
                if (!togglePin.isPending) togglePin.mutate()
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  e.stopPropagation()
                  if (!togglePin.isPending) togglePin.mutate()
                }
              }}
              className={
                "grid h-6 w-6 shrink-0 cursor-pointer place-items-center rounded-md transition hover:bg-plum-200/60 disabled:opacity-40 dark:hover:bg-white/10 " +
                (pinned
                  ? "text-plum-500"
                  : "text-plum-700/40 opacity-0 group-hover:opacity-100 focus:opacity-100 hover:text-plum-700/70 dark:text-plum-200/35")
              }
            >
              <Pin className={"h-3.5 w-3.5 " + (pinned ? "fill-plum-500" : "")} />
            </span>
          )}
          <span className="min-w-0 flex-1" />
          {stamp && (
            <span className="shrink-0 text-[10.5px] text-plum-700/45 dark:text-plum-200/35">
              {stamp}
            </span>
          )}
        </span>
        <span className="mt-0.5 block truncate text-[12px] text-plum-700/55 dark:text-plum-200/40">
          {subtitle}
        </span>
      </span>
    </button>
  )
}

function channelActivityAt(c: Conversation): string {
  return c.lastMessage?.createdAt ?? c.modifiedAt ?? c.createdAt ?? ""
}

function Glyph({
  emoji,
  guest,
  muted,
}: {
  emoji: string | null
  guest: boolean
  muted: boolean
}) {
  if (emoji) {
    return (
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-plum-100 text-[18px] dark:bg-white/10">
        {emoji}
      </span>
    )
  }
  if (guest) {
    return (
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-plum-100 text-[var(--accent)] dark:bg-white/10 dark:text-plum-200/70">
        {muted ? <Megaphone className="h-4 w-4" /> : <Hash className="h-4 w-4" />}
      </span>
    )
  }
  return (
    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-plum-50 text-plum-700/45 dark:bg-white/5 dark:text-plum-200/35">
      <Hash className="h-4 w-4" />
    </span>
  )
}

function EmptyState({
  hasAny,
  archived,
  onNew,
}: {
  hasAny: boolean
  archived: boolean
  onNew: () => void
}) {
  return (
    <div className="flex flex-col items-center px-6 py-16 text-center">
      <div className="mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-plum-100/70 text-plum-700/70 dark:bg-white/5 dark:text-plum-200/55">
        <Hash className="h-5 w-5" />
      </div>
      <h2 className="text-[15px] font-semibold text-plum-900 dark:text-plum-50">
        {archived ? "No archived channels" : hasAny ? "No matches" : "No channels"}
      </h2>
      <p className="mt-1.5 max-w-xs text-[13px] leading-relaxed text-plum-700/55 dark:text-plum-200/45">
        {archived
          ? "Archived channels show up here."
          : hasAny
            ? "Try a different name."
            : "Channels are shared spaces in the attendee chat tab."}
      </p>
      {!hasAny && !archived && (
        <button
          onClick={onNew}
          className="mt-4 flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-[13px] font-semibold text-white shadow-fluent-sm transition hover:brightness-110"
          style={{ background: "var(--accent)" }}
        >
          <Plus className="h-4 w-4" />
          New channel
        </button>
      )}
    </div>
  )
}
